import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  EventDetailsRequest,
  EventDetailsResponse,
  GetPerformanceAvailabilitiesRequest,
  GetPerformanceAvailabilitiesResponse,
  GetPerformancesRequest,
  GetPerformancesResponse,
  ReserveTicketsRequest,
  ReserveTicketsResponse,
} from '@grpc-clients/external/mercury/v1/entertainment/entertainment.pb';
import { BookClient } from '@grpc-clients/services/booking-service/api/book.pbsc';
import { PrebookClient } from '@grpc-clients/services/booking-service/api/prebook.pbsc';
import { EntertainmentRedirectRequest } from '@grpc-clients/services/booking-service/dto/entertainment-redirect-request.pb';
import { EntertainmentClient } from '@grpc-clients/services/entertainments-service/api/entertainment.pbsc';
import { GrpcDataEvent, GrpcStatusEvent } from '@ngx-grpc/common';
import { Empty } from '@ngx-grpc/well-known-types';
import { EntertainmentTicketApiErrorComponent } from '@shared/components/error/entertainment-ticket-api-error/entertainment-ticket-api-error.component';
import { ReserveTicketsInfo } from '@shared/store/entertainment';
import { Observable, catchError, map, of, pairwise, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class EntertainmentTicketDetailsService {
  constructor(
    private searchClient: EntertainmentClient,
    public bookClient: BookClient,
    private preBookClient: PrebookClient,
    private snackBar: MatSnackBar
  ) {}

  public getEventDetails(language: string, eventId: number): Observable<EventDetailsResponse> {
    // tenantId, brandId and salesChannelId is added by BE
    const request = new EventDetailsRequest({
      languageCode: language,
      eventId,
    });

    return this.searchClient.eventDetails(request).pipe(
      map((response: EventDetailsResponse) => response),
      catchError(() => of(null))
    );
  }

  public getEventPerformance(request: GetPerformancesRequest): Observable<GetPerformancesResponse> {
    // tenantId, brandId and salesChannelId is added by BE
    return this.searchClient.getPerformances(request).pipe(
      map((response: GetPerformancesResponse) => response),
      catchError(() => of(null))
    );
  }

  public getEventPerformanceAvailability(
    request: GetPerformanceAvailabilitiesRequest
  ): Observable<GetPerformanceAvailabilitiesResponse> {
    // tenantId, brandId and salesChannelId is added by BE

    return this.searchClient.getPerformanceAvailabilities(request).pipe(
      map((response: GetPerformanceAvailabilitiesResponse) => response),
      catchError(() => of(null))
    );
  }

  public redirect(request: EntertainmentRedirectRequest): Observable<Empty> {
    return this.bookClient.entertainmentRedirect(request).pipe(
      map((response: Empty) => response),
      catchError(() => of(null))
    );
  }

  public reserveTicket(language, currency, request: ReserveTicketsRequest): Observable<ReserveTicketsInfo> {
    // tenantId, brandId and salesChannelId is added by BE
    const payload = new ReserveTicketsRequest({
      performanceId: request.performanceId,
      tickets: request.tickets,
      languageCode: { value: language },
      currencyCode: currency,
    });

    return this.preBookClient.$raw.mHReserveTickets(payload).pipe(
      tap((statusEvent) => {
        if (statusEvent['statusCode'] === 13 || !!statusEvent['statusMessage']) {
          setTimeout(() => {
            this.snackBar.openFromComponent(EntertainmentTicketApiErrorComponent, {
              duration: 5000,
            });
          });
          return of(null);
        }
      }),
      pairwise(),
      map(([statusEvent, dataEvent]: [GrpcStatusEvent, GrpcDataEvent<ReserveTicketsResponse>]) => ({
        data: dataEvent.data,
        paymentToken: statusEvent.metadata.get('payment_token'),
        performanceId: request.performanceId,
      }))
    );
  }
}
